import React, { useState } from 'react';
import { HelpCircle, MessageSquare, FileText, ChevronDown, Send, ExternalLink } from 'lucide-react';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
interface SupportTabProps {
  theme: ZodiacTheme;
  setActiveTab: (tab: string) => void;
}
const faqs = [{
  q: 'Как проходит консультация с астрологом?',
  a: 'Консультация проходит в выбранном вами формате: видео-звонок, аудио-звонок или текстовый чат. В назначенное время вы подключаетесь по ссылке в разделе «Мои консультации».'
}, {
  q: 'Как вернуть деньги за заказ?',
  a: 'Если астролог ещё не начал работу — отмените заказ в разделе «Мои заказы» с полным возвратом. Если работа начата — возврат рассматривается индивидуально через поддержку.'
}, {
  q: 'Как добавить профиль родственника?',
  a: 'Перейдите в «Мой астропрофиль» → «Добавить профиль». Сохраните данные близких для быстрого оформления заказов.'
}, {
  q: 'Как работает баланс?',
  a: 'Баланс — ваш кошелёк в рублях. Пополняйте картой, Apple Pay или Google Pay. При оплате средства списываются автоматически. Если не хватает — можно доплатить.'
}];
const docs = [{
  label: 'Правила платформы',
  href: '#'
}, {
  label: 'Политика возвратов',
  href: '#'
}, {
  label: 'Конфиденциальность',
  href: '#'
}];
export function SupportTab({
  theme,
  setActiveTab
}: SupportTabProps) {
  const {
    showToast
  } = useToast();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [message, setMessage] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    showToast('Сообщение отправлено в поддержку');
    setMessage('');
  };
  return <div className="space-y-5 max-w-2xl pb-8" data-id="element-19462">
      {/* Header */}
      <div data-id="element-19463">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19464">Помощь</h2>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-19465">
          Ответы на вопросы и связь с поддержкой
        </p>
      </div>

      {/* FAQ */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-19466">
        <div className="px-4 py-3 border-b border-slate-100" data-id="element-19467">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19468">
            <HelpCircle className="w-4 h-4 text-slate-400" data-id="element-19469" /> Частые вопросы
          </h3>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-19470">
          {faqs.map((faq, idx) => <div key={idx} data-id="element-19471">
              <button className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50/50 transition-colors" onClick={() => setOpenFaq(openFaq === idx ? null : idx)} data-id="element-19472">
                <span className="text-sm font-medium text-slate-900" data-id="element-19473">
                  {faq.q}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-19474" />
              </button>
              {openFaq === idx && <div className="px-4 pb-3 -mt-1" data-id="element-19475">
                  <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 rounded-lg p-3" data-id="element-19476">
                    {faq.a}
                  </p>
                </div>}
            </div>)}
        </div>
      </div>

      {/* Contact Support */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3" data-id="element-19477">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19478">
          <MessageSquare className="w-4 h-4 text-slate-400" data-id="element-19479" /> Написать в
          поддержку
        </h3>
        <p className="text-xs text-slate-500" data-id="element-19480">
          Ежедневно с 09:00 до 21:00 по МСК. Обычно отвечаем за 15 минут.
        </p>
        <form onSubmit={handleSubmit} className="space-y-2" data-id="element-19481">
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Опишите вашу проблему..." className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm min-h-[80px] resize-none" required data-id="element-19482" />
          <div className="flex items-center justify-between gap-3" data-id="element-19483">
            <button type="button" onClick={() => setActiveTab('chat')} className="text-xs text-indigo-600 hover:underline" data-id="element-19484">
              Открыть чат →
            </button>
            <Button type="submit" size="sm" className="text-xs px-4" data-id="element-19485">
              <Send className="w-3.5 h-3.5 mr-1.5" data-id="element-19486" /> Отправить
            </Button>
          </div>
        </form>
      </div>

      {/* Documents */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-19487">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2 mb-3" data-id="element-19488">
          <FileText className="w-4 h-4 text-slate-400" data-id="element-19489" /> Документы
        </h3>
        <div className="flex flex-wrap gap-2" data-id="element-19490">
          {docs.map(doc => <a key={doc.label} href={doc.href} className="flex items-center gap-1.5 px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-xs font-medium text-slate-700 transition-colors" data-id="element-19491">
              <ExternalLink className="w-3 h-3 text-slate-400" data-id="element-19492" /> {doc.label}
            </a>)}
        </div>
      </div>
    </div>;
}